import { MAX_IMAGES_PER_MEDIA } from "@coworker/app/src/components/RoomSettings/constants";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { AddImagesModal } from "@coworker/apprestructured/src/task-management/components/AddImagesModal/AddImagesModal";
import { useTranslation } from "@coworker/locales";
import { useMemo, useState } from "react";
import { FullscreenImagePopup } from "../../../shared/components/ImageViewer/FullscreenImagePopup/FullscreenImagePopup";
import ClickableButtonWrapper from "../../../shared/wrappers/ClickableButtonWrapper/ClickableButtonWrapper";
import FixaCarousel from "../../../shared/wrappers/FixaCarousel/FixaCarousel";
import FixaImage from "../../../shared/wrappers/FixaImage/FixaImage";
import styles from "./media-detail-carousel.module.css";
import { useAddImagesModal } from "./useAddImagesModal";

interface MediaDetailCarouselProps {
  images: string[];
  title: string;
  imageAlt?: string;
}

export const MediaDetailCarousel = ({
  images,
  title,
  imageAlt,
}: MediaDetailCarouselProps) => {
  const { t } = useTranslation();
  const [openedImageIndex, setOpenedImageIndex] = useState<number>();

  const {
    addImagesModalOpen,
    setAddImagesModalOpen,
    modalImages,
    setModalImages,
  } = useAddImagesModal(images);

  const preparedImages = useMemo(() => {
    const getImageAlt = (index: number) =>
      index === 0 && imageAlt ? imageAlt : `${title} image ${index + 1}`;

    return images.map((src, index) => ({
      src,
      alt: getImageAlt(index),
    }));
  }, [images, imageAlt, title]);

  return (
    <div className={styles["carouselWrapper"]}>
      <FixaCarousel
        id="media-detail-carousel"
        className={styles["carousel"]}
        ariaLabelLeftBtn={t("previousImageString")}
        ariaLabelRightBtn={t("nextImageString")}
      >
        {preparedImages.map(({ src, alt }, index) => (
          <ClickableButtonWrapper
            key={[src, index].join("-")}
            aria-label={`Image ${index + 1} button`}
            onClick={() => setOpenedImageIndex(index)}
          >
            <FixaImage
              className={styles["carouselImage"]}
              src={src}
              alt={alt}
            />
          </ClickableButtonWrapper>
        ))}
      </FixaCarousel>

      <FixaButton
        className={styles["addImagesButton"]}
        htmlType="button"
        text={t("addImagesString")}
        type="secondary"
        onClick={() => setAddImagesModalOpen(true)}
      />

      <AddImagesModal
        isOpen={addImagesModalOpen}
        images={modalImages}
        onImagesChange={setModalImages}
        onClose={() => {
          setAddImagesModalOpen(false);
        }}
        onConfirmClick={() => {}}
        maxImages={MAX_IMAGES_PER_MEDIA}
      />

      <FullscreenImagePopup
        images={preparedImages}
        isOpen={openedImageIndex !== undefined}
        onClose={() => setOpenedImageIndex(undefined)}
        slideIndex={openedImageIndex ?? 0}
      />
    </div>
  );
};
